import { supabase } from '../config/supabaseConfig.ts';

// Conectores (integraciones) y herramientas de los agentes. Script 38 de smooth-stats-dashboard/supabase/security:
// las claves se guardan en Vault por RPC (connector_save) y nunca vuelven al navegador; las herramientas tienen borrador y versiones.

const CONNECTOR_COLS = 'id, public_id, project_id, kind, name, config, last4, status, last_error, last_checked_at, created_at, updated_at';
const TOOL_COLS = 'id, public_id, name, slug, scope, project_id, agent_id, draft, published_version, status, created_at, updated_at';

/** Si el script 38 no se ha corrido, las tablas no existen: se avisa con un código propio en vez de un error de SQL. */
const missing38 = (error) => /connectors|tools|tool_versions|schema cache|does not exist|connector_save/i.test(error?.message || '');
function fail(error) {
  if (missing38(error)) { const e = new Error('Falta correr el script 38 (conectores y herramientas) en Supabase.'); e.code = 'missing_38'; throw e; }
  throw error;
}

export const toolsService = {
  // ── Conectores ──
  async listConnectors() {
    const { data, error } = await supabase.from('connectors').select(CONNECTOR_COLS).order('name');
    if (error) fail(error);
    return data || [];
  },
  async saveConnector({ id = null, projectId = null, kind, name, config, secret }) {
    const { data, error } = await supabase.rpc('connector_save', { p_id: id, p_project: projectId, p_kind: kind, p_name: name, p_config: config, p_secret: secret || null });
    if (error) fail(error);
    return data;
  },
  async deleteConnector(id) {
    const { error } = await supabase.rpc('connector_delete', { p_id: id });
    if (error) fail(error);
  },

  // ── Herramientas ──
  async listTools() {
    const { data, error } = await supabase.from('tools').select(TOOL_COLS).order('name');
    if (error) fail(error);
    return data || [];
  },
  async getTool(id) {
    const { data, error } = await supabase.from('tools').select(TOOL_COLS).eq('id', id).maybeSingle();
    if (error) fail(error);
    if (!data) return null;
    const { data: versions, error: e2 } = await supabase.from('tool_versions').select('id, version, definition, published_at').eq('tool_id', id).order('version', { ascending: false });
    if (e2) fail(e2);
    return { ...data, versions: versions || [] };
  },
  async createTool({ name, slug, scope, agentId = null, draft }) {
    const { data, error } = await supabase.from('tools').insert({ name, slug, scope, agent_id: scope === 'personal' ? agentId : null, draft }).select(TOOL_COLS).single();
    if (error) {
      if (/tools_slug_check/.test(error.message)) throw new Error('El nombre para el agente solo lleva minúsculas, números y guion bajo, y empieza con letra.');
      fail(error);
    }
    return data;
  },
  async updateTool(id, patch) {
    const { error } = await supabase.from('tools').update(patch).eq('id', id);
    if (error) {
      if (/tools_slug_check/.test(error.message)) throw new Error('El nombre para el agente solo lleva minúsculas, números y guion bajo, y empieza con letra.');
      fail(error);
    }
  },
  async publishTool(id) {
    const { data, error } = await supabase.rpc('tool_publish', { p_tool: id });
    if (error) fail(error);
    return data;
  },
  /** La definición PUBLICADA de varias herramientas (la que usa el motor), por id de herramienta. */
  async publishedDefinitions(tools) {
    const list = tools.filter((t) => t.published_version);
    if (!list.length) return {};
    const { data, error } = await supabase.from('tool_versions').select('tool_id, version, definition').in('tool_id', list.map((t) => t.id));
    if (error) fail(error);
    const out = {};
    for (const t of list) { const v = (data || []).find((x) => x.tool_id === t.id && x.version === t.published_version); if (v) out[t.id] = v.definition; }
    return out;
  },
  /**
   * Qué agentes la tienen asignada: en su versión publicada (la usan hoy) o solo en el borrador (la usarán al publicar).
   * Una herramienta publicada se aplica en el siguiente mensaje de los agentes publicados, sin volver a publicarlos.
   */
  async toolUsage(toolId) {
    const { data, error } = await supabase.from('agent_versions').select('agent_id, state, version, agents(id, name, status, published_version, project_id)')
      .in('state', ['draft', 'published']).contains('config', { custom_tools: [{ tool_id: toolId }] });
    if (error) fail(error);
    const byAgent = {};
    for (const v of data || []) {
      const a = v.agents; if (!a || a.status === 'archived') continue;
      const live = v.state === 'published' && v.version === a.published_version;
      const cur = byAgent[a.id] || { id: a.id, name: a.name, live: false, draft: false };
      if (live) cur.live = true; if (v.state === 'draft') cur.draft = true;
      byAgent[a.id] = cur;
    }
    return Object.values(byAgent).sort((x, y) => Number(y.live) - Number(x.live) || x.name.localeCompare(y.name, 'es'));
  },
  // ── Ejecuciones (script 40) ──
  async listToolRuns(toolId, { before, limit = 40 } = {}) {
    let q = supabase.from('tool_runs').select('id, created_at, source, agent_id, tested_by, status, ms, tool_version, chat_id, error, agents(name)')
      .eq('tool_id', toolId).order('id', { ascending: false }).limit(limit);
    if (before) q = q.lt('id', before);
    const { data, error } = await q;
    if (error) { if (/tool_runs|schema cache|does not exist/i.test(error.message)) { const e = new Error('Falta correr el script 40 (ejecuciones de herramientas) en Supabase.'); e.code = 'missing_40'; throw e; } fail(error); }
    return data || [];
  },
  async getToolRun(id) {
    const { data, error } = await supabase.from('tool_runs').select('*, agents(name)').eq('id', id).maybeSingle();
    if (error) fail(error);
    return data;
  },
  async runRetention(toolId) {
    const { data, error } = await supabase.from('tools').select('run_retention_days').eq('id', toolId).maybeSingle();
    if (error) return null;
    return data?.run_retention_days ?? 7;
  },
  async setRunRetention(toolId, days) {
    const { error } = await supabase.from('tools').update({ run_retention_days: days }).eq('id', toolId);
    if (error) fail(error);
  },
  async deleteTool(id) {
    const { error } = await supabase.from('tools').delete().eq('id', id);
    if (error) fail(error);
  },
};
